import { CheckCircle2, Circle } from "lucide-react"
import { useSearchParams } from "react-router-dom"
import { Progress } from "@/components/ui/progress"
import { cn } from "@/lib/utils"
import { firstFocusableControl } from "@/lib/focus-target"
import { indicatorLabel } from "@/lib/qualiopi-indicator-label"
import { useOrganisationToday } from "@/hooks/use-organisation-day"
import type { CompletenessState } from "./EntityCompletenessContext"

const FLASH_DURATION_MS = 3000
const FLASH_CLASSES = ["ring-2", "ring-warning", "ring-offset-2"]

// Exported so other surfaces (publish readiness summary, PricingSection, etc.)
// route to a field with the same scroll + focus + flash behavior. Honors
// prefers-reduced-motion (WCAG 2.3.3) and moves keyboard focus, not just the
// viewport (WCAG 2.4.3).
export function scrollToField(targetId: string) {
  const el = document.getElementById(targetId)
  if (!el) {
    return
  }
  const prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches
  el.scrollIntoView({
    behavior: prefersReducedMotion ? "auto" : "smooth",
    block: "center",
  })
  const focusable = firstFocusableControl(el)
  if (focusable) {
    focusable.focus({ preventScroll: true })
  }
  el.classList.add(...FLASH_CLASSES)
  el.dataset.testFlashing = "true"
  window.setTimeout(() => {
    el.classList.remove(...FLASH_CLASSES)
    delete el.dataset.testFlashing
  }, FLASH_DURATION_MS)
}

function bucketForScore(percent: number): { color: string; label: string } {
  if (percent >= 80) return { color: "text-emerald-600", label: "Bon" }
  if (percent >= 50) return { color: "text-amber-600", label: "À compléter" }
  return { color: "text-rose-600", label: "Insuffisant" }
}

/** Titre par défaut. La fiche face au référentiel, sans verdict. */
const COMPLETENESS_PANEL_TITLE = "Adéquation au référentiel Qualiopi"

export function EntityCompletenessPanel({ state }: { state: CompletenessState }) {
  const { entityName, entityId, data, fieldChecks, title = COMPLETENESS_PANEL_TITLE } = state
  const today = useOrganisationToday()

  const totalWeight = fieldChecks.reduce((acc, check) => acc + (check.weight ?? 1), 0)
  const completedWeight = data
    ? fieldChecks.reduce(
        (acc, check) => acc + (check.isComplete(data, today) ? (check.weight ?? 1) : 0),
        0,
      )
    : 0
  const percent = totalWeight === 0 ? 0 : Math.round((completedWeight / totalWeight) * 100)
  const completedCount = data
    ? fieldChecks.filter((check) => check.isComplete(data, today)).length
    : 0
  const totalCount = fieldChecks.length
  const bucket = bucketForScore(percent)
  const containerTestId = `completeness-sidebar-${entityName}`
  const [, setSearchParams] = useSearchParams()

  // Route the click through the URL: activate the hosting tab (if the target
  // lives on a non-default tab that Radix would otherwise keep unmounted) and
  // flag the target via `focus`. On a detail page that mounts
  // useFocusFieldFromUrl (e.g. StaffDetailPage), the hook then switches the tab,
  // scrolls to the target, and InlineEditableField opens itself in edit mode.
  //
  // The immediate scrollToField call is the graceful fallback for detail pages
  // that don't (yet) mount the hook: when the target is already in the DOM it
  // scrolls right away, preserving the pre-existing behavior for every entity.
  function focusCheck(check: CompletenessState["fieldChecks"][number]) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (check.tab) {
          next.set("tab", check.tab)
        }
        next.set("focus", check.scrollTargetId)
        return next
      },
      { replace: true }
    )
    scrollToField(check.scrollTargetId)
  }

  return (
    <div
      className="border-t border-border/60 px-3 py-3"
      data-test={containerTestId}
      data-entity-name={entityName}
      data-entity-id={entityId}
      data-score={percent}
    >
      <div className="mb-2 flex items-center justify-between gap-2 px-1">
        <span
          className="min-w-0 text-xs font-semibold uppercase tracking-wider text-muted-foreground"
          data-test={`${containerTestId}-title`}
        >
          {title}
        </span>
        <span
          className={cn("shrink-0 whitespace-nowrap text-sm font-semibold tabular-nums", bucket.color)}
          data-test={`${containerTestId}-score`}
        >
          {percent} %
        </span>
      </div>
      <div className="mb-2 flex items-center justify-between gap-2 px-1 text-[11px] text-muted-foreground">
        <span data-test={`${containerTestId}-bucket`}>{bucket.label}</span>
        <span data-test={`${containerTestId}-ratio`}>
          {completedCount}/{totalCount} critères
        </span>
      </div>
      <Progress
        value={percent}
        className="mb-2 h-1"
        aria-label={`${title} : ${percent} %`}
        data-test={`${containerTestId}-progress`}
      />
      <ul className="space-y-0.5" role="list">
        {fieldChecks.map((check) => {
          const complete = data ? check.isComplete(data, today) : false
          const reason =
            !complete && data && check.incompleteReason ? check.incompleteReason(data) : null
          return (
            <li key={check.fieldPath}>
              <button
                type="button"
                onClick={() => focusCheck(check)}
                className={cn(
                  "flex w-full items-start gap-2 rounded-md px-2 py-1 text-left text-xs transition-colors hover:bg-accent/50",
                  complete ? "text-foreground" : "text-muted-foreground"
                )}
                data-test={`${containerTestId}-field-${check.fieldPath}`}
                data-complete={complete ? "true" : "false"}
              >
                {complete ? (
                  <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-emerald-500" />
                ) : (
                  <Circle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-rose-400" />
                )}
                <span className="flex-1 leading-tight">
                  {check.label}
                  {check.qualiopiIndicator !== undefined && (
                    <span className="ml-1 text-[10px] text-muted-foreground">
                      · {indicatorLabel(check.qualiopiIndicator, "I")}
                    </span>
                  )}
                  {typeof check.weight === "number" && check.weight > 1 && (
                    <span className="ml-1 text-[10px] text-muted-foreground/70">
                      · {check.weight} pts
                    </span>
                  )}
                  {reason && (
                    <span
                      className="mt-0.5 block text-[10px] leading-tight text-amber-600"
                      data-test={`${containerTestId}-reason-${check.fieldPath}`}
                    >
                      {reason}
                    </span>
                  )}
                </span>
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
